import React, { useState } from 'react';
import { Document as BaseDocument, Page, pdfjs } from 'react-pdf';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Skeleton } from 'antd';
import classnames from 'classnames';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

export default function Document({
  file,
  loading,
  defaultPage,
  defaultScale,
  onChangePage,
  placeholder,
  hideControls,
  className,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:PDF:Document' });

  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(defaultPage ?? 1);
  const [scale, setScale] = useState(defaultScale ?? 1);
  const [renderedPageNumber, setRenderedPageNumber] = useState(null);
  const [renderedScale, setRenderedScale] = useState(null);
  // Unscaled page dimensions, captured on load, used to reserve the container's
  // footprint (size × scale) so it resizes in one step instead of collapsing
  // while react-pdf repaints the canvas.
  const [pageSize, setPageSize] = useState(null);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }

  function changePage(offset) {
    setPageNumber((prevPageNumber) => prevPageNumber + offset);
    onChangePage && onChangePage(pageNumber + offset);
  }

  function previousPage() {
    changePage(-1);
  }

  function nextPage() {
    changePage(1);
  }

  function changeScale(offset) {
    setScale((prevScale) => prevScale + offset);
  }

  function decreaseScale() {
    changeScale(-0.1);
  }

  function increaseScale() {
    changeScale(0.1);
  }

  const isLoading = renderedPageNumber !== pageNumber || renderedScale !== scale;
  // While the new page renders, keep the previously-rendered page in normal flow
  // so the container holds its size; the new page renders as an absolute overlay
  // and swaps in once painted (prevents the width collapsing/jumping mid-render).
  const showPreviousPage = isLoading && Boolean(renderedPageNumber) && Boolean(renderedScale);

  const controlColor = '#5F6974';

  const containerStyle = pageSize ? { width: pageSize.width * scale, height: pageSize.height * scale } : undefined;

  return (
    <div className={classnames('flex flex-col', className)}>
      {loading && (placeholder ?? <Skeleton active paragraph={{ rows: 15 }} className="w-[520px]" />)}
      {!loading &&
        (file ? (
          <>
            {!hideControls && (
              <Flex justify="center" className="mb-md">
                <Flex>
                  <Button
                    type="text"
                    size="small"
                    style={scale <= 0.5 ? undefined : { color: controlColor }}
                    disabled={scale <= 0.5}
                    onClick={decreaseScale}
                  >
                    -
                  </Button>
                  <p className="my-auto ml-xs">0.5</p>
                </Flex>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.1"
                  value={scale}
                  onChange={(event) => setScale(Number(event.target.value))}
                  className="mx-md"
                />
                <Flex>
                  <p className="my-auto mr-xs">2</p>
                  <Button
                    type="text"
                    size="small"
                    style={scale >= 2 ? undefined : { color: controlColor }}
                    disabled={scale >= 2}
                    onClick={increaseScale}
                  >
                    +
                  </Button>
                </Flex>
              </Flex>
            )}
            <BaseDocument file={file} onLoadSuccess={onDocumentLoadSuccess}>
              <div className="relative flex justify-center mx-auto" style={containerStyle}>
                {showPreviousPage ? (
                  <div className="absolute inset-0 flex justify-center">
                    <Page
                      key={`${renderedPageNumber}@${renderedScale}`}
                      pageNumber={renderedPageNumber}
                      scale={renderedScale}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </div>
                ) : null}
                <Page
                  key={`${pageNumber}@${scale}`}
                  pageNumber={pageNumber}
                  scale={scale}
                  onLoadSuccess={(page) =>
                    setPageSize({
                      width: page.originalWidth,
                      height: page.originalHeight,
                    })
                  }
                  onRenderSuccess={() => {
                    setRenderedPageNumber(pageNumber);
                    setRenderedScale(scale);
                  }}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </div>
            </BaseDocument>
            {!hideControls && (
              <Flex justify="center" className="mt-md">
                {numPages !== 1 && (
                  <Button
                    type="text"
                    style={pageNumber <= 1 ? undefined : { color: controlColor }}
                    disabled={pageNumber <= 1}
                    onClick={previousPage}
                  >
                    {t('buttons.previous')}
                  </Button>
                )}
                <p className="my-auto mx-md">
                  {t('labels.page', {
                    current: pageNumber || (numPages ? 1 : '--'),
                    total: numPages || '--',
                  })}
                </p>
                {numPages !== 1 && (
                  <Button
                    type="text"
                    style={pageNumber >= numPages ? undefined : { color: controlColor }}
                    disabled={pageNumber >= numPages}
                    onClick={nextPage}
                  >
                    {t('buttons.next')}
                  </Button>
                )}
              </Flex>
            )}
          </>
        ) : (
          <h5 className="w-full text-center">{t('labels.no_file_specified')}</h5>
        ))}
    </div>
  );
}
